import { useState, useEffect, useRef } from 'react'
import styles from './AddWorklogModal.module.css'
import apiClient from '../../utils/api'
import DescriptionAutofill from '../DescriptionAutofill'
import useEscapeKey from '../../hooks/useEscapeKey'
import { moment } from '../../utils/dateUtils'
import { parseMentions } from '../../utils/mentionParser'

export default function CalendarWorklogModal({
    isOpen,
    onClose,
    onSave,
    onDelete,
    initialData = null,
    isEditMode = false,
    teammates = [],
    isAdmin = false,
    currentUser = null,
    availableProjects = [],
    availableTasks = [],
    apiToken = null
}) {
    const [formData, setFormData] = useState({
        description: '',
        startTime: '',
        endTime: '',
        date: '',
        project: '',
        task: '',
        userId: null, // For teammate selection
        billable: true
    })

    // State for user-specific projects and tasks
    const [userProjects, setUserProjects] = useState([])
    const [loadingProjects, setLoadingProjects] = useState(false)
    const [projectTasks, setProjectTasks] = useState({}) // Store tasks by project ID
    const [loadingTasks, setLoadingTasks] = useState(false)

    // State for custom dropdown
    const [showProjectDropdown, setShowProjectDropdown] = useState(false)
    const projectDropdownRef = useRef(null)
    const descriptionInputRef = useRef(null)

    // Refs for focus management
    const dateInputRef = useRef(null)
    const saveButtonRef = useRef(null)

    // Handle escape key to close modal
    useEscapeKey(isOpen, onClose)

    // Use user-specific projects when admin has selected a specific user
    const projects = (isAdmin && formData.userId && formData.userId !== currentUser?.id)
        ? userProjects  // Use user-specific projects (even if empty)
        : availableProjects  // Use default projects for current user

    // Initialize form data when modal opens
    useEffect(() => {
        if (isOpen && initialData) {
            const formatTimeForInput = (date) => {
                if (!date) return ''
                const momentDate = moment(date)
                if (!momentDate.isValid()) return ''
                // Use HH:mm format (24-hour format without seconds)
                return momentDate.format('HH:mm')
            }

            const formatDateForInput = (date) => {
                if (!date) return ''
                const momentDate = moment(date)
                if (!momentDate.isValid()) return ''
                return momentDate.format('YYYY-MM-DD')
            }

            setFormData({
                description: initialData.title || '',
                startTime: formatTimeForInput(initialData.startTime),
                endTime: formatTimeForInput(initialData.endTime),
                date: formatDateForInput(initialData.date || initialData.startTime),
                project: initialData.project?.id || initialData.project || '',
                task: initialData.task?.id || initialData.task || '',
                userId: initialData.userId || (currentUser?.id || null),
                billable: initialData.billable !== undefined ? initialData.billable : true
            })
        } else if (isOpen) {
            // Reset form for new worklog with default times
            // Use project's billable value if available, otherwise default to true
            const projectBillable = initialData?.project?.is_billable !== undefined
                ? initialData.project.is_billable
                : true

            setFormData({
                description: '',
                startTime: '00:00',
                endTime: '00:00',
                date: '',
                project: '',
                task: '',
                userId: currentUser?.id || null,
                billable: projectBillable
            })
        } else if (!isOpen) {
            // Clear tasks cache when modal closes to avoid stale data
            setProjectTasks({})
        }
    }, [isOpen, initialData, isEditMode, currentUser])

    // Focus description input when modal opens for new worklog
    useEffect(() => {
        if (isOpen && !isEditMode && descriptionInputRef.current) {
            // Small delay to ensure modal is fully rendered
            const timer = setTimeout(() => {
                descriptionInputRef.current.focus()
            }, 100)
            return () => clearTimeout(timer)
        }
    }, [isOpen, isEditMode])

    // Load projects for specific user
    const loadUserProjects = async (userId) => {
        if (!apiToken) return

        setLoadingProjects(true)
        try {
            apiClient.setAuthToken(apiToken)
            const projectsData = await apiClient.getProjects(userId)
            const allProjects = projectsData.projects || []
            // Filter to only include active projects
            const activeProjects = allProjects.filter(p => p.status === 'active')
            setUserProjects(activeProjects)
        } catch (error) {
            console.error('Failed to load user projects:', error)
            setUserProjects([])
        } finally {
            setLoadingProjects(false)
        }
    }

    // Load tasks for a specific project
    const loadProjectTasks = async (projectId) => {
        if (!apiToken || !projectId) return Promise.resolve()

        if (projectTasks[projectId]) return Promise.resolve()

        setLoadingTasks(true)
        try {
            apiClient.setAuthToken(apiToken)
            const response = await apiClient.getTasks(projectId)
            const tasks = response.tasks || []
            setProjectTasks(prev => ({
                ...prev,
                [projectId]: tasks
            }))
            console.log(`Loaded ${tasks.length} tasks for project ${projectId}:`, tasks)
            return Promise.resolve(tasks)
        } catch (error) {
            console.error('Failed to load project tasks:', error)
            setProjectTasks(prev => ({
                ...prev,
                [projectId]: []
            }))
            return Promise.reject(error)
        } finally {
            setLoadingTasks(false)
        }
    }

    // Load projects when assigned user changes
    useEffect(() => {
        if (isOpen && formData.userId && isAdmin && apiToken) {
            loadUserProjects(formData.userId)
        } else {
            // Clear user-specific data when no specific user is selected
            setUserProjects([])
        }
    }, [formData.userId, isOpen, isAdmin, apiToken])

    // Load tasks when modal opens and there's already a project selected (for edit mode)
    useEffect(() => {
        if (isOpen && formData.project && apiToken) {
            loadProjectTasks(formData.project)
        }
    }, [isOpen, formData.project, apiToken])

    // Handle clicking outside dropdown to close it
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (projectDropdownRef.current && !projectDropdownRef.current.contains(event.target)) {
                setShowProjectDropdown(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    // Parse description for auto-population of project and task
    const parseDescription = (description) => {
        // Format: <description>@<project>@<task>
        // Example: "Standup@Modi@Dev"
        const match = description.match(/^(.+?)@(.+?)(?:@(.+))?$/)

        if (!match) return null

        const [, desc, projectPattern, taskPattern] = match

        return {
            description: desc.trim(),
            projectPattern: projectPattern?.trim(),
            taskPattern: taskPattern?.trim()
        }
    }

    // Find best matching project based on pattern
    const findMatchingProject = (pattern, projectsList) => {
        if (!pattern) return null

        const lowerPattern = pattern.toLowerCase()

        // Try exact match first (case-insensitive)
        const exactMatch = projectsList.find(p =>
            p.name.toLowerCase() === lowerPattern
        )
        if (exactMatch) return exactMatch

        // Try starts-with match
        const startsWithMatch = projectsList.find(p =>
            p.name.toLowerCase().startsWith(lowerPattern)
        )
        if (startsWithMatch) return startsWithMatch

        // Try contains match
        const containsMatch = projectsList.find(p =>
            p.name.toLowerCase().includes(lowerPattern)
        )

        return containsMatch
    }

    // Find best matching task based on pattern
    const findMatchingTask = (pattern, tasksList) => {
        if (!pattern || !tasksList || tasksList.length === 0) return null

        const lowerPattern = pattern.toLowerCase()

        // Try exact match first (case-insensitive)
        const exactMatch = tasksList.find(t =>
            t.name.toLowerCase() === lowerPattern
        )
        if (exactMatch) return exactMatch

        // Try starts-with match
        const startsWithMatch = tasksList.find(t =>
            t.name.toLowerCase().startsWith(lowerPattern)
        )
        if (startsWithMatch) return startsWithMatch

        // Try contains match
        const containsMatch = tasksList.find(t =>
            t.name.toLowerCase().includes(lowerPattern)
        )

        return containsMatch
    }

    // Capitalize first letter of a sentence
    const capitalizeFirstLetter = (text) => {
        if (!text) return text
        return text.charAt(0).toUpperCase() + text.slice(1)
    }

    // Handle Tab key press for auto-population
    const handleDescriptionKeyDown = async (e) => {
        if (e.key === 'Tab' && formData.description) {
            e.preventDefault()

            const parsed = parseDescription(formData.description)

            if (parsed && parsed.projectPattern) {
                const matchedProject = findMatchingProject(parsed.projectPattern, projects)

                if (matchedProject) {
                    // Set the description without the pattern (capitalized)
                    // Also set billable based on project
                    setFormData(prev => ({
                        ...prev,
                        description: capitalizeFirstLetter(parsed.description),
                        project: matchedProject.id,
                        task: '', // Reset task, will be set below if pattern exists
                        billable: matchedProject.is_billable !== undefined ? matchedProject.is_billable : prev.billable
                    }))

                    // Load tasks for the matched project
                    if (apiToken) {
                        try {
                            const loadedTasks = await loadProjectTasks(matchedProject.id)

                            // Try to match task if pattern exists
                            if (parsed.taskPattern && loadedTasks) {
                                const matchedTask = findMatchingTask(parsed.taskPattern, loadedTasks)

                                if (matchedTask) {
                                    setFormData(prev => ({
                                        ...prev,
                                        task: matchedTask.id,
                                        billable: matchedTask.is_billable !== undefined ? matchedTask.is_billable : prev.billable
                                    }))
                                }
                            }
                        } catch (error) {
                            console.error('Failed to load tasks for auto-population:', error)
                        }
                    }

                    // Pattern with @ found - move focus to save button
                    setTimeout(() => {
                        saveButtonRef.current?.focus()
                    }, 100)
                } else {
                    // No project match found, just capitalize the description
                    setFormData(prev => ({
                        ...prev,
                        description: capitalizeFirstLetter(parsed.description)
                    }))

                    // Pattern with @ found but no match - move focus to save button
                    setTimeout(() => {
                        saveButtonRef.current?.focus()
                    }, 100)
                }
            } else {
                // No pattern found, just capitalize the entire description
                setFormData(prev => ({
                    ...prev,
                    description: capitalizeFirstLetter(formData.description)
                }))

                // No @ pattern - move focus to date input for manual entry
                setTimeout(() => {
                    dateInputRef.current?.focus()
                }, 100)
            }
        }
    }

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }))

        // Auto-set billable based on task selection
        if (field === 'task') {
            if (value) {
                // Find the selected task and use its billable status
                const selectedTask = getAvailableTasks().find(t => t.id === value)
                if (selectedTask && selectedTask.is_billable !== undefined) {
                    setFormData(prev => ({
                        ...prev,
                        billable: selectedTask.is_billable
                    }))
                }
            } else {
                const selectedProject = projects.find(p => p.id === formData.project)
                if (selectedProject && selectedProject.is_billable !== undefined) {
                    setFormData(prev => ({
                        ...prev,
                        billable: selectedProject.is_billable
                    }))
                }
            }
        }

        // Reset task when project changes and load tasks for the new project
        if (field === 'project') {
            setFormData(prev => ({
                ...prev,
                task: ''
            }))

            // Auto-set billable based on project selection
            if (value) {
                const selectedProject = projects.find(p => p.id === value)
                if (selectedProject && selectedProject.is_billable !== undefined) {
                    setFormData(prev => ({
                        ...prev,
                        billable: selectedProject.is_billable
                    }))
                }

                // Load tasks for the selected project
                if (apiToken) {
                    loadProjectTasks(value)
                }
            }
        }

        // Load user-specific projects when user changes
        if (field === 'userId' && isAdmin && apiToken) {
            if (value) {
                loadUserProjects(value)
            } else {
                setUserProjects([])
            }
            // Reset project and task when user changes
            setFormData(prev => ({
                ...prev,
                project: '',
                task: ''
            }))
        }
    }

    const handleDescriptionSuggestionSelect = async (suggestion) => {
        console.log('Suggestion selected:', suggestion)

        // Auto-populate description and billable status immediately
        const updatedFormData = {
            ...formData,
            description: suggestion.description,
            billable: suggestion.billable !== undefined ? suggestion.billable : formData.billable
        }

        // Find and set the project
        if (suggestion.project_id && projects.length > 0) {
            const matchedProject = projects.find(p => p.id === suggestion.project_id)
            if (matchedProject) {
                updatedFormData.project = matchedProject.id
                console.log('Setting project:', matchedProject.name, matchedProject.id)

                // Load tasks for this project if not already loaded and there's a task to set
                if (apiToken && suggestion.task_id && !projectTasks[matchedProject.id]) {
                    console.log('Loading tasks for project:', matchedProject.id)

                    // Set initial data without task first
                    setFormData(updatedFormData)

                    // Load tasks and then set the task
                    try {
                        const loadedTasks = await loadProjectTasks(matchedProject.id)

                        // Directly check if the task exists in the loaded tasks
                        if (loadedTasks && loadedTasks.length > 0) {
                            const matchedTask = loadedTasks.find(t => t.id === suggestion.task_id)
                            if (matchedTask) {
                                setFormData(prev => ({
                                    ...prev,
                                    task: suggestion.task_id
                                }))
                                console.log('Setting task after loading:', matchedTask.name, suggestion.task_id)
                            } else {
                                console.log('Task not found in loaded tasks:', suggestion.task_id)
                            }
                        }

                    } catch (error) {
                        console.error('Failed to load tasks for auto-population:', error)
                    }
                    return // Exit early since we're handling async task loading
                }
            }
        }

        // Find and set the task if present (synchronous path)
        if (suggestion.task_id) {
            console.log('Looking for task:', suggestion.task_id)

            // First check if tasks are already loaded for this project
            if (projectTasks[suggestion.project_id]) {
                const matchedTask = projectTasks[suggestion.project_id].find(t => t.id === suggestion.task_id)
                if (matchedTask) {
                    updatedFormData.task = matchedTask.id
                    console.log('Found task in projectTasks:', matchedTask.name)
                }
            } else {
                const matchedProject = projects.find(p => p.id === suggestion.project_id)
                if (matchedProject && matchedProject.tasks) {
                    const matchedTask = matchedProject.tasks.find(t => t.id === suggestion.task_id)
                    if (matchedTask) {
                        updatedFormData.task = matchedTask.id
                        console.log('Found task in project.tasks:', matchedTask.name)
                    }
                } else {
                    const matchedTask = availableTasks.find(t => t.id === suggestion.task_id)
                    if (matchedTask) {
                        updatedFormData.task = matchedTask.id
                        console.log('Found task in availableTasks:', matchedTask.name)
                    }
                }
            }
        } else {
            updatedFormData.task = ''
        }

        console.log('Final updated form data:', updatedFormData)
        setFormData(updatedFormData)
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        // Validation
        if (!formData.description.trim()) {
            alert('Please enter a description')
            return
        }
        if (!formData.startTime || !formData.endTime || !formData.date) {
            alert('Please fill in all time and date fields')
            return
        }
        if (!formData.project) {
            alert('Please select a project')
            return
        }

        // **FIX: Parse time in HH:mm format (24-hour format without seconds)**
        const startTimeParts = formData.startTime.split(':').map(Number)
        const [startHour, startMinute] = startTimeParts
        const endTimeParts = formData.endTime.split(':').map(Number)
        const [endHour, endMinute] = endTimeParts

        const startTime = moment(formData.date)
            .hour(startHour)
            .minute(startMinute)
            .second(0)
            .millisecond(0)
            .toDate()

        const endTime = moment(formData.date)
            .hour(endHour)
            .minute(endMinute)
            .second(0)
            .millisecond(0)
            .toDate()

        // Handle overnight shifts
        if (moment(endTime).isSameOrBefore(moment(startTime))) {
            endTime.setDate(endTime.getDate() + 1)
        }

        // Create normalized date (start of day)
        const worklogDate = moment(formData.date).startOf('day').toDate()

        // Clean hashtags from description using parseMentions
        const { cleanDescription } = parseMentions(
            formData.description,
            availableProjects,
            availableTasks
        )

        const worklogData = {
            id: initialData?.id, // Include ID for editing
            title: cleanDescription || formData.description,  // Use cleaned description
            startTime,
            endTime,
            date: worklogDate,
            project: formData.project,
            task: formData.task,
            userId: formData.userId,
            billable: formData.billable,
            type: 'work',
        }

        onSave(worklogData)
        onClose()
    }

    const handleCancel = () => {
        onClose()
    }

    const handleDelete = () => {
        if (window.confirm('Are you sure you want to delete this worklog?')) {
            onDelete(initialData.id)
            onClose()
        }
    }

    // Helper function to check if the time entry spans overnight
    const isOvernightEntry = () => {
        if (!formData.startTime || !formData.endTime) return false

        const startTimeParts = formData.startTime.split(':').map(Number)
        const [startHour, startMinute] = startTimeParts
        const endTimeParts = formData.endTime.split(':').map(Number)
        const [endHour, endMinute] = endTimeParts

        const startSeconds = startHour * 3600 + startMinute * 60
        const endSeconds = endHour * 3600 + endMinute * 60

        return endSeconds <= startSeconds
    }

    const getAvailableTasks = () => {
        const projectId = formData.project
        if (!projectId) return []

        // First try to get tasks from the projectTasks state (dynamically loaded)
        if (projectTasks[projectId]) {
            return projectTasks[projectId]
        }

        // Fallback: try to get tasks from the project if they're embedded
        const selectedProject = projects.find(project => project.id === projectId)
        if (selectedProject?.tasks) {
            return selectedProject.tasks
        }

        // Fallback: filter availableTasks by project ID
        return availableTasks.filter(task => task.project_id === projectId)
    }

    // Compute all available tasks including dynamically loaded ones for autocomplete
    const getAllTasks = () => {
        // Start with the initial availableTasks
        const taskMap = new Map()
        availableTasks.forEach(task => {
            taskMap.set(task.id, task)
        })

        // Add all dynamically loaded tasks from projectTasks
        Object.values(projectTasks).forEach(taskArray => {
            if (Array.isArray(taskArray)) {
                taskArray.forEach(task => {
                    taskMap.set(task.id, task)
                })
            }
        })

        return Array.from(taskMap.values())
    }

    if (!isOpen) return null

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <h2>{isEditMode ? 'Edit Worklog' : 'Add Worklog'}</h2>
                    <button
                        className={styles.closeButton}
                        onClick={handleCancel}
                        aria-label="Close modal"
                    >
                        ×
                    </button>
                </div>

                <form onSubmit={handleSubmit} className={styles.form}>
                    {/* Frozen Fields Section */}
                    <div className={styles.frozenFieldsSection}>
                        {/* Assign To - Frozen field */}
                        <div className={styles.frozenFieldGroup}>
                            <label className={styles.frozenLabel}>Assign to:</label>
                            <div className={styles.frozenText}>
                                {(() => {
                                    const user = formData.userId
                                        ? (teammates.find(t => t.id === formData.userId) || currentUser)
                                        : currentUser
                                    return user?.display_name || user?.first_name || user?.email || 'Current User'
                                })()}
                            </div>
                        </div>

                        {/* Date - Frozen field */}
                        <div className={styles.frozenFieldGroup}>
                            <label className={styles.frozenLabel}>Date:</label>
                            <div className={styles.frozenText}>
                                {formData.date ? moment(formData.date).format('dddd, MMMM D, YYYY') : 'No date selected'}
                            </div>
                        </div>

                        {/* Billable - Frozen field */}
                        <div className={styles.frozenFieldGroup}>
                            <label className={styles.frozenLabel}>Billable:</label>
                            <div className={styles.frozenText}>
                                {formData.billable ? 'Yes' : 'No'}
                            </div>
                        </div>
                    </div>

                    {/* Description */}
                    <div className={styles.formGroup}>
                        <label htmlFor="description" className={styles.label}>
                            Description *
                        </label>
                        <DescriptionAutofill
                            ref={descriptionInputRef}
                            value={formData.description}
                            onChange={(value) => handleInputChange('description', value)}
                            onSuggestionSelect={handleDescriptionSuggestionSelect}
                            onKeyDown={handleDescriptionKeyDown}
                            placeholder="Enter description. Tip: Use #project #task @<user> format"
                            className={styles.input}
                            apiToken={apiToken}
                            userId={formData.userId}
                            projects={availableProjects}
                            tasks={getAllTasks()}
                            teammates={teammates}
                            selectedProject={availableProjects.find(p => p.id === formData.project)}
                            onProjectTaskMention={({type, item}) => {
                                if (type === 'project') {
                                    handleInputChange('project', item.id)
                                    if (item.is_billable !== undefined) {
                                        handleInputChange('billable', item.is_billable)
                                    }
                                } else if (type === 'task') {
                                    // Set both project and task in a single update to avoid race condition
                                    const taskProject = availableProjects.find(p => p.id === item.project_id)

                                    // Update formData with both project and task at once
                                    setFormData(prev => ({
                                        ...prev,
                                        task: item.id,
                                        project: taskProject ? taskProject.id : prev.project,
                                        billable: item.is_billable !== undefined ? item.is_billable : prev.billable
                                    }))

                                    // Load tasks for the project if needed
                                    if (taskProject && apiToken) {
                                        loadProjectTasks(taskProject.id)
                                    }
                                }
                            }}
                        />
                    </div>

                    {/* Project Selection - EDITABLE */}
                    <div className={styles.formGroup}>
                        <label htmlFor="project" className={styles.label}>
                            Project *
                        </label>
                        <select
                            id="project"
                            className={styles.select}
                            value={formData.project || ''}
                            onChange={(e) => handleInputChange('project', e.target.value)}
                            required
                        >
                            <option value="">Select a project</option>
                            {projects.map(project => (
                                <option key={project.id} value={project.id}>
                                    {project.name}{project.client_name ? ` - ${project.client_name}` : ''}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Task Selection - EDITABLE */}
                    <div className={styles.formGroup}>
                        <label htmlFor="task" className={styles.label}>
                            Task (Optional)
                        </label>
                        <select
                            id="task"
                            className={styles.select}
                            value={formData.task || ''}
                            onChange={(e) => handleInputChange('task', e.target.value)}
                            disabled={!formData.project}
                        >
                            <option value="">No task</option>
                            {getAvailableTasks().map(task => (
                                <option key={task.id} value={task.id}>
                                    {task.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Time Inputs */}
                    <div className={styles.timeInputsRow}>
                        <div className={styles.formGroup}>
                            <label htmlFor="startTime" className={styles.label}>
                                Start Time *
                            </label>
                            <input
                                type="time"
                                id="startTime"
                                className={styles.timeInput}
                                value={formData.startTime}
                                onChange={(e) => handleInputChange('startTime', e.target.value)}
                                required
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="endTime" className={styles.label}>
                                End Time *
                                {isOvernightEntry() && (
                                    <span className={styles.overnightIndicator}> +1</span>
                                )}
                            </label>
                            <input
                                type="time"
                                id="endTime"
                                className={styles.timeInput}
                                value={formData.endTime}
                                onChange={(e) => handleInputChange('endTime', e.target.value)}
                                required
                            />
                        </div>
                    </div>

                    <div className={styles.buttonRow}>
                        <div className={styles.leftButtons}>
                            {isEditMode && (
                                <button
                                    type="button"
                                    className={styles.deleteButton}
                                    onClick={handleDelete}
                                >
                                    Delete
                                </button>
                            )}
                        </div>
                        <div className={styles.rightButtons}>
                            <button
                                type="button"
                                className={styles.cancelButton}
                                onClick={handleCancel}
                            >
                                Cancel
                            </button>
                            <button
                                ref={saveButtonRef}
                                type="submit"
                                className={styles.saveButton}
                            >
                                {isEditMode ? 'Update Worklog' : 'Save Worklog'}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    )
}
